import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { batchService, ProductBatch } from '../../../services/batch.service'
import { toast } from 'sonner'
import { Edit, Trash2, Plus, X, Save, AlertTriangle, CheckCircle, FilterX } from 'lucide-react'
import { GlobalPaginator } from '../../ui/global-paginator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { DateRangeFilter } from '../../ui/DateRangeFilter'
import { DateRange } from 'react-day-picker'
import { format } from 'date-fns'
import { ConfirmationModal } from '../../ui/ConfirmationModal'
import { FormError } from '../../ui/FormError'

interface BatchesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: number | null
  productName: string
}

// ─── Esquemas de validación ─────────────────────────────────────────────────

const batchSchema = z.object({
  batch_number: z.string().min(1, 'El número de lote es requerido'),
  expiration_date: z.string().min(1, 'La fecha de expiración es requerida')
})

type BatchFormValidate = z.infer<typeof batchSchema>

export function BatchesModal({ open, onOpenChange, productId, productName }: BatchesModalProps) {
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [dateRange, setDateRange] = useState<DateRange | undefined>()

  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)


  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null)
  const [selectedBatchNumber, setSelectedBatchNumber] = useState('')

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<BatchFormValidate>({
    resolver: zodResolver(batchSchema),
    defaultValues: {
      batch_number: '',
      expiration_date: ''
    }
  })

  React.useEffect(() => {
    if (open) {
      setIsAdding(false)
      setEditingId(null)
      reset({ batch_number: '', expiration_date: '' })
    }
  }, [open, reset])

  const { data: response, isLoading } = useQuery({
    queryKey: ['batches', productId, page, filterStatus, dateRange],
    queryFn: () => batchService.getBatches(productId!, page, {
      status: filterStatus,
      from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    }),
    enabled: !!productId && open,
  })

  const batches = response?.data ?? []
  const paginationMeta = {
    total: response?.total ?? 0,
    from: response?.from ?? 0,
    to: response?.to ?? 0,
  }

  const createMutation = useMutation({
    mutationFn: (data: BatchFormValidate) => batchService.createBatch(productId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches', productId] })
      setIsAdding(false)
      reset({ batch_number: '', expiration_date: '' })
      toast.success('Lote agregado correctamente')
    },
    onError: () => toast.error('Error al agregar el lote')
  })

  const updateMutation = useMutation({
    mutationFn: ({ batchId, data }: { batchId: number, data: BatchFormValidate }) =>
      batchService.updateBatch(productId!, batchId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches', productId] })
      setEditingId(null)
      reset({ batch_number: '', expiration_date: '' })
      toast.success('Lote actualizado')
    },
    onError: () => toast.error('Error al actualizar el lote')
  })

  const deleteMutation = useMutation({
    mutationFn: (batchId: number) => batchService.deleteBatch(productId!, batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches', productId] })
      setDeleteConfirmOpen(false)
      setSelectedBatchId(null)
      setSelectedBatchNumber('')
      toast.success('Lote eliminado correctamente')
    },
    onError: () => toast.error('Error al eliminar el lote')
  })

  const handleEditClick = (batch: ProductBatch) => {
    setEditingId(batch.id)
    setIsAdding(false)
    reset({
      batch_number: batch.batch_number,
      expiration_date: batch.expiration_date.split('T')[0] // Assuming YYYY-MM-DD
    })
  }

  const handleCancel = () => {
    setIsAdding(false)
    setEditingId(null)
    reset({ batch_number: '', expiration_date: '' })
  }

  const handleSave = (data: BatchFormValidate) => {
    if (editingId) {
      updateMutation.mutate({ batchId: editingId, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const getExpirationStatus = (dateString: string) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Parse strictly local time to avoid timezone offset shifts
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number)
    const exp = new Date(year, month - 1, day)

    if (exp < today) return 'expirado'
    if (exp.getTime() === today.getTime()) return 'hoy'
    return 'vigente'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit(handleSave)}>
          <DialogHeader>
            <DialogTitle>Gestión de Lotes</DialogTitle>
            <DialogDescription>
              Producto: <span className="font-semibold text-slate-800">{productName}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex flex-wrap gap-2 flex-1">
                <Select value={filterStatus} onValueChange={(val) => { setFilterStatus(val); setPage(1) }}>
                  <SelectTrigger className="w-[160px] bg-white">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="vigente">Vigentes</SelectItem>
                    <SelectItem value="expirado">Expirados</SelectItem>
                  </SelectContent>
                </Select>

                <DateRangeFilter
                  date={dateRange}
                  onChange={(d) => { setDateRange(d); setPage(1) }}
                  placeholder="Rango de caducidad"
                  className="bg-white"
                />

                {(filterStatus !== 'all' || dateRange) && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => { setFilterStatus('all'); setDateRange(undefined); setPage(1) }}
                    className="h-10 px-3 text-slate-500 hover:text-slate-800"
                  >
                    <FilterX className="h-4 w-4 mr-2" />
                    Limpiar
                  </Button>
                )}
              </div>

              {!isAdding && !editingId && (
                <Button type="button" onClick={() => { setIsAdding(true); reset({ batch_number: '', expiration_date: '' }) }} size="sm" className="shrink-0">
                  <Plus className="mr-2 h-4 w-4" /> Agregar Lote
                </Button>
              )}
            </div>

            {(isAdding || editingId) && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex gap-4 items-end">
                <div className="space-y-2 flex-1">
                  <label className="text-xs font-medium text-slate-700">Número de Lote</label>
                  <Input
                    {...register('batch_number')}
                    placeholder="Ej: LOTE-123"
                  />
                  <FormError message={errors?.batch_number?.message} />
                </div>
                <div className="space-y-2 flex-1">
                  <label className="text-xs font-medium text-slate-700">Fecha de Caducidad</label>
                  <Input
                    type="date"
                    {...register('expiration_date')}
                  />
                  <FormError message={errors?.expiration_date?.message} />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="icon" onClick={handleCancel}>
                    <X className="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button type="submit" size="icon" disabled={createMutation.isPending || updateMutation.isPending}>
                    <Save className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número de Lote</TableHead>
                    <TableHead>Caducidad</TableHead>
                    <TableHead>Cantidad</TableHead>
                    <TableHead>Sucursal</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4 text-slate-500">Cargando lotes...</TableCell>
                    </TableRow>
                  ) : batches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">No hay lotes registrados.</TableCell>
                    </TableRow>
                  ) : (
                    batches.map((batch: ProductBatch) => {
                      const status = getExpirationStatus(batch.expiration_date)
                      const rowClass = status === 'expirado' ? 'bg-red-50/50' : status === 'hoy' ? 'bg-yellow-50/50' : ''
                      const dateFormatted = batch.expiration_date.split('T')[0].split('-').reverse().join('/')

                      return (
                        <TableRow key={batch.id} className={rowClass}>
                          <TableCell className="font-medium text-slate-700">{batch.batch_number}</TableCell>
                          <TableCell className={status === 'expirado' ? 'text-red-600' : status === 'hoy' ? 'text-yellow-700 font-medium' : ''}>
                            {dateFormatted}
                          </TableCell>
                          <TableCell className="font-medium">{batch.quantity ?? 0}</TableCell>
                          <TableCell className="text-slate-600">{batch.sucursal?.name ?? 'N/A'}</TableCell>
                          <TableCell>
                            {status === 'expirado' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">
                                <AlertTriangle className="h-3 w-3" /> Expirado
                              </span>
                            ) : status === 'hoy' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                                <AlertTriangle className="h-3 w-3" /> Por Expirar Hoy
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                <CheckCircle className="h-3 w-3" /> Vigente
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditClick(batch)}
                              disabled={editingId === batch.id || isAdding}
                            >
                              <Edit className="h-4 w-4 text-primary" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setDeleteConfirmOpen(true)
                                setSelectedBatchId(batch.id)
                                setSelectedBatchNumber(batch.batch_number)
                              }}
                              disabled={editingId === batch.id || isAdding || deleteMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            {paginationMeta.total > 10 && (
              <div className="mt-4">
                <GlobalPaginator
                  totalItems={paginationMeta.total}
                  itemsPerPage={10}
                  currentPage={page}
                  onPageChange={setPage}
                  from={paginationMeta.from}
                  to={paginationMeta.to}
                />
              </div>
            )}
          </div>
        </form>
      </DialogContent>

      {/* ── Modal: Confirmar Eliminar Producto ── */}
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setSelectedBatchId(null) }}
        onConfirm={() => selectedBatchId && deleteMutation.mutate(selectedBatchId)}
        preset="delete"
        itemName={`Lote "${selectedBatchNumber}"`}
      />

    </Dialog>
  )
}
